import { useMediaQuery } from 'react-responsive';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import {
  CardDragglableProps,
  CardStaticProps,
} from '@/components/shared/CourseAdminCard/types';
import { Badge } from '@/components/ui/daisy';
import { useConfirm } from '@/data/context/ConfirmModalContext';
import Exam from '@/models/Exam';
import { Breakpoints } from '@/utils/Breakpoint';
import ExamTypeBadge from './ExamTypeBadge';

type ExamCardProps = {
  exam: Exam;
  ordinal: number; //TODO: remover essa prop quando o back implementar order na model de Exam
  onEdit?: (examId: number) => void;
  onDelete?: (examId: number) => Promise<void>;
  className?: string;
  isLimitedEdition?: boolean;
} & (CardStaticProps | CardDragglableProps);

export default function ExamCard({
  exam,
  ordinal,
  onEdit,
  onDelete,
  isLimitedEdition = false,
  ...cardProps
}: ExamCardProps) {
  const confirm = useConfirm();
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });

  const handleDelete = () => {
    confirm({
      onConfirm: () => onDelete?.(exam.id),
      variant: 'destructive',
    });
  };

  return (
    <CourseAdminCard {...cardProps}>
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading>
          <CourseAdminCard.Order value={ordinal} />
          <CourseAdminCard.TitleBlock>
            <CourseAdminCard.Title>{exam.name}</CourseAdminCard.Title>
            {exam.description && (
              <CourseAdminCard.Description>
                {exam.description}
              </CourseAdminCard.Description>
            )}
            <CourseAdminCard.Badges>
              <ExamTypeBadge type={exam.type} size={isXl ? 'md' : 'sm'} />
              <Badge size={isXl ? 'md' : 'sm'} variant="soft">
                {exam.defaultTime} min
              </Badge>
              {exam.assessmentItem && (
                <Badge size={isXl ? 'md' : 'sm'} variant="soft">
                  {exam.assessmentItem.name}
                </Badge>
              )}
            </CourseAdminCard.Badges>
          </CourseAdminCard.TitleBlock>
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          {onEdit && (
            <CourseAdminCard.EditButton onClick={() => onEdit(exam.id)} />
          )}
          {onDelete && (
            <CourseAdminCard.MoreActions>
              <CourseAdminCard.MoreActions.Delete
                onClick={handleDelete}
                disabled={isLimitedEdition}
              />
            </CourseAdminCard.MoreActions>
          )}
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
    </CourseAdminCard>
  );
}
